import {
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormCheck,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSmartPagination,
  CSpinner,
} from '@coreui/react-pro'
import MoreVert from '@mui/icons-material/MoreVert'
import Search from '@mui/icons-material/Search'
import { IconButton } from '@mui/material'
import axios from 'axios'
import { useContext, useEffect, useCallback, useState } from 'react'
import Avatar from 'react-avatar'
import { useLocation, useNavigate } from 'react-router-dom'
import { businessApiURL } from 'src/business/services'
import { ThemeContext } from 'src/context/ThemeContext'
import { getCustomerDetails } from 'src/services/APIs/Customer/CustomerDetails'
import { getCompanyEmployees } from '../../services/APIs/Subscriber/getUserData'
import { MembersContainer } from '../UserProfile/styledComponents'
import './membersPage.css'
import BulkActionForm from './bulkActionsForm'
import { MembersProgressBar } from './customComponents/MembersProgressBar'
import ProfileView from './profileView'
import {
  ActionBtn,
  EmptyState,
  EmptyStateText,
  EmptyStateTitle,
  MemberEmail,
  MemberName,
  MembersPageWrapper,
  PaginationBar,
  PaginationInfo,
  PageSizeSelect,
  SearchInput,
  StatCard,
  StatLabel,
  StatValue,
  StatusBadge,
  TableCard,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TableWrapper,
  StyledTable,
  Toolbar,
  ToolbarActions,
} from './MembersPageStyles'

const PROFILE_IMG_BASE = 'https://media.aumhum.com/images/prod/'

function formatStatus(status) {
  if (status === 'renew') return 'Renewed'
  if (status === 'expire') return 'Expired'
  return status ? status[0].toUpperCase() + status.slice(1) : '—'
}

function StatusPill({ status }) {
  const cls = status === 'renew' ? 'renew' : (status || '').toLowerCase()
  return <StatusBadge className={cls}>{formatStatus(status)}</StatusBadge>
}

const Table = ({ profile }) => {
  const location = useLocation()
  const { theme } = useContext(ThemeContext)
  const [items, setItems] = useState([])
  const [details, setDetails] = useState({
    subscribers: 0,
    licenceQuota: 0,
    licenseQuota: 0,
  })
  const [selected, setSelected] = useState([])
  const [visible, setVisible] = useState(false)
  const [filteredResults, setFilteredResults] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [profileViewOpen, setProfileViewOpen] = useState(false)
  const token = localStorage.getItem('token')
  const [profileId, setProfileId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 0,
    offset: 10,
  })
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const navigate = useNavigate()
  const [loadingSubscribers, setLoadingSubscribers] = useState(false)

  const quota = details.licenseQuota ?? details.licenceQuota ?? 0
  const subscribers = details.subscribers ?? 0
  const atCapacity = subscribers >= quota && quota > 0

  const userData = useCallback(async () => {
    setLoadingSubscribers(true)
    try {
      const response = await getCompanyEmployees(pagination.pageNumber + 1, pagination.pageSize)
      const data = response?.data
      const content = data?.content ?? []
      setItems(content)
      setFilteredResults(content)
      setPagination((p) => ({ ...p, ...(data?.pageable ?? {}) }))
      setTotalElements(data?.totalElements ?? content.length)
      setTotalPages(data?.totalPages ?? 1)
    } finally {
      setLoadingSubscribers(false)
    }
  }, [pagination.pageNumber, pagination.pageSize])

  const setData = useCallback(
    async (pageNumber, pageSize = pagination.pageSize) => {
      setLoadingSubscribers(true)
      try {
        const response = await getCompanyEmployees(pageNumber + 1, pageSize)
        const data = response?.data
        const content = data?.content ?? []
        setPagination((p) => ({ ...p, pageNumber, pageSize, ...(data?.pageable ?? {}) }))
        setTotalElements(data?.totalElements ?? content.length)
        setTotalPages(data?.totalPages ?? 1)
        setItems(content)
        setFilteredResults(content)
      } finally {
        setLoadingSubscribers(false)
      }
    },
    [pagination.pageSize],
  )

  const changePageSize = (e) => {
    const newSize = Number(e.target.value) || 10
    const newOffset = pagination.pageNumber * newSize
    let updatedPage = pagination.pageNumber - Math.ceil((newOffset - pagination.offset) / pagination.pageSize)
    if (updatedPage < 0) updatedPage = 0
    setData(updatedPage, newSize)
  }

  const changePageNumber = (value) => {
    setData(value - 1)
  }

  const searchItems = (searchValue) => {
    setSearchInput(searchValue)
    const q = (searchValue || '').toLowerCase().trim()
    if (!q) {
      setFilteredResults([...items])
      return
    }
    setFilteredResults(
      items.filter((item) => {
        const m = item?.businessMember
        const name = (m?.name || '').toLowerCase()
        const email = (m?.email || '').toLowerCase()
        return name.includes(q) || email.includes(q)
      }),
    )
  }

  useEffect(() => {
    if (items.length) {
      const q = (searchInput || '').toLowerCase().trim()
      if (!q) setFilteredResults([...items])
      else
        setFilteredResults(
          items.filter((item) => {
            const m = item?.businessMember
            const name = (m?.name || '').toLowerCase()
            const email = (m?.email || '').toLowerCase()
            return name.includes(q) || email.includes(q)
          }),
        )
    }
  }, [items])

  const check = (e, id) => {
    const checked = e.target.checked
    setFilteredResults((prev) =>
      prev.map((item) =>
        item.businessMember.id === id ? { ...item, _selected: checked } : item,
      ),
    )
    setSelected((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)))
  }

  const selectAllSubs = (e) => {
    const checked = e.target.checked
    setFilteredResults((prev) => prev.map((item) => ({ ...item, _selected: checked })))
    setSelected(checked ? filteredResults.map((item) => item.businessMember.id) : [])
  }

  const activate = (subsId) => {
    axios
      .get(`${businessApiURL}/api/secure/bc/subscriber/${subsId}/activate`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => userData())
  }

  const deactivate = (subsId) => {
    axios
      .get(`${businessApiURL}/api/secure/bc/subscriber/${subsId}/expire`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => userData())
  }

  const renew = (subsId) => {
    axios
      .get(`${businessApiURL}/api/secure/bc/subscriber/${subsId}/renew`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => userData())
  }

  const scrollToHash = () => {
    const id = location.hash?.replace('#', '')
    if (id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToHash()
  }, [location.hash, loading])

  useEffect(() => {
    setLoading(true)
    Promise.all([
      getCompanyEmployees(1, pagination.pageSize).then((r) => {
        const d = r?.data
        const content = d?.content ?? []
        setItems(content)
        setFilteredResults(content)
        setPagination((p) => ({ ...p, ...(d?.pageable ?? {}) }))
        setTotalElements(d?.totalElements ?? 0)
        setTotalPages(d?.totalPages ?? 1)
      }),
      getCustomerDetails().then((r) => setDetails(r?.data ?? {})),
    ]).finally(() => setLoading(false))
  }, [])

  const start = pagination.pageNumber * (pagination.pageSize || 10)
  const end = Math.min(start + filteredResults.length, totalElements)
  const hasResults = filteredResults.length > 0
  const showingZero = loadingSubscribers ? false : !hasResults

  return (
    <MembersContainer theme={theme} id="members">
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CSpinner />
        </div>
      ) : (
        <>
          <CModal
            visible={visible}
            onClose={() => setVisible(false)}
            className="members-bulk-modal"
          >
            <CModalHeader onClose={() => setVisible(false)}>
              <CModalTitle>Bulk Actions</CModalTitle>
            </CModalHeader>
            <CModalBody>
              <BulkActionForm updateSubscribers={userData} selected={selected} />
            </CModalBody>
            <CModalFooter>
              <ActionBtn className="secondary" onClick={() => setVisible(false)}>
                Close
              </ActionBtn>
            </CModalFooter>
          </CModal>

          <CModal
            alignment="center"
            visible={profileViewOpen}
            onClose={() => setProfileViewOpen(false)}
            className="members-profile-modal"
          >
            <CModalHeader onClose={() => setProfileViewOpen(false)}>
              <CModalTitle>Member Profile</CModalTitle>
            </CModalHeader>
            <CModalBody>
              <ProfileView profile={profileId} />
            </CModalBody>
          </CModal>

          <MembersPageWrapper>
            <StatCard style={{ flex: '1 1 100%' }}>
              <StatLabel>Members used</StatLabel>
              <StatValue>
                {subscribers} <span style={{ fontWeight: 400, fontSize: 16, color: '#5c5c7a' }}>/ {quota} seats</span>
              </StatValue>
            </StatCard>

            <MembersProgressBar completed={subscribers} maxCompleted={quota || 1} />

            <Toolbar>
              <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 220 }}>
                <Search
                  sx={{
                    position: 'absolute',
                    left: 14,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9a9ab0',
                    fontSize: 20,
                  }}
                />
                <SearchInput
                  type="search"
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => searchItems(e.target.value)}
                  style={{ paddingLeft: 44 }}
                />
              </div>
              <ToolbarActions>
                <ActionBtn
                  className="primary"
                  onClick={() => navigate('add-subscriber')}
                  disabled={atCapacity}
                >
                  + Add Member
                </ActionBtn>
                <ActionBtn className="secondary" onClick={() => setVisible(true)}>
                  Bulk actions {selected.length > 0 ? `(${selected.length})` : ''}
                </ActionBtn>
                <ActionBtn className="secondary" onClick={() => navigate('../addBulkSubscriber')}>
                  + Bulk add
                </ActionBtn>
              </ToolbarActions>
            </Toolbar>

            <TableCard>
              {loadingSubscribers ? (
                <div style={{ padding: 48, textAlign: 'center' }}>
                  <CSpinner />
                </div>
              ) : hasResults ? (
                <>
                  <TableWrapper>
                    <StyledTable>
                      <TableHead>
                        <tr>
                          <TableHeadCell>
                            <CFormCheck onChange={selectAllSubs} />
                          </TableHeadCell>
                          <TableHeadCell />
                          <TableHeadCell>Name</TableHeadCell>
                          <TableHeadCell>Email</TableHeadCell>
                          <TableHeadCell>Package</TableHeadCell>
                          <TableHeadCell>Status</TableHeadCell>
                          <TableHeadCell>Joined</TableHeadCell>
                          <TableHeadCell />
                        </tr>
                      </TableHead>
                      <tbody>
                        {filteredResults.map((item) => (
                          <TableRow key={item.businessMember.id}>
                            <TableCell>
                              <CFormCheck
                                id={`cb-${item.businessMember.id}`}
                                checked={!!item._selected}
                                onChange={(e) => check(e, item.businessMember.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Avatar
                                name={item.businessMember.name}
                                src={PROFILE_IMG_BASE + (item.userProfileResponse?.profilePicId || '')}
                                size="44"
                                round
                              />
                            </TableCell>
                            <TableCell>
                              <MemberName>{item.businessMember.name || '—'}</MemberName>
                            </TableCell>
                            <TableCell>
                              <MemberEmail>
                                {(item.businessMember?.email || '')
                                  .replace('@aumhum.xyz', '@vfcorp.com') || '—'}
                              </MemberEmail>
                            </TableCell>
                            <TableCell>
                              <MemberEmail>{item.packageName || '—'}</MemberEmail>
                            </TableCell>
                            <TableCell>
                              <StatusPill status={item.businessMember.status} />
                            </TableCell>
                            <TableCell>
                              <MemberEmail>{item.businessMember.createdTime || '—'}</MemberEmail>
                            </TableCell>
                            <TableCell>
                              <CDropdown variant="dropdown">
                                <CDropdownToggle carettag="span" custom>
                                  <IconButton size="small" aria-label="Actions">
                                    <MoreVert />
                                  </IconButton>
                                </CDropdownToggle>
                                <CDropdownMenu>
                                  <CDropdownItem onClick={() => activate(item.businessMember.id)}>
                                    Activate
                                  </CDropdownItem>
                                  <CDropdownItem onClick={() => deactivate(item.businessMember.id)}>
                                    Deactivate
                                  </CDropdownItem>
                                  <CDropdownItem onClick={() => renew(item.businessMember.id)}>
                                    Renew
                                  </CDropdownItem>
                                  <CDropdownItem
                                    onClick={() => {
                                      setProfileId(item)
                                      setProfileViewOpen(true)
                                    }}
                                  >
                                    View Profile
                                  </CDropdownItem>
                                </CDropdownMenu>
                              </CDropdown>
                            </TableCell>
                          </TableRow>
                        ))}
                      </tbody>
                    </StyledTable>
                  </TableWrapper>

                  <PaginationBar>
                    <PaginationInfo>
                      Showing {start + 1}–{end} of {totalElements}
                    </PaginationInfo>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <CSmartPagination
                        className="m-0"
                        align="center"
                        activePage={pagination.pageNumber + 1}
                        pages={totalPages}
                        onActivePageChange={changePageNumber}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <PageSizeSelect
                          value={pagination.pageSize}
                          onChange={changePageSize}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </PageSizeSelect>
                        <span style={{ fontSize: 13, color: '#5c5c7a' }}>per page</span>
                      </div>
                    </div>
                  </PaginationBar>
                </>
              ) : (
                <EmptyState>
                  <EmptyStateTitle>
                    {searchInput.trim() ? 'No members match your search' : 'No members yet'}
                  </EmptyStateTitle>
                  <EmptyStateText>
                    {searchInput.trim()
                      ? 'Try a different search term.'
                      : 'Add your first member to get started.'}
                  </EmptyStateText>
                  {!searchInput.trim() && (
                    <ActionBtn
                      className="primary"
                      style={{ marginTop: 16 }}
                      onClick={() => navigate('add-subscriber')}
                      disabled={atCapacity}
                    >
                      + Add Member
                    </ActionBtn>
                  )}
                </EmptyState>
              )}
            </TableCard>
          </MembersPageWrapper>
        </>
      )}
    </MembersContainer>
  )
}

export default Table
